
















































































































































import {Component, Prop, Vue, Watch} from "vue-property-decorator";
import axios from "axios";
import {LocationEfsta} from "@/shared/LocationEfsta";
import JSZip from "jszip";
import {saveAs} from "file-saver";

interface UniqueSite extends LocationEfsta {
  reportCount: number;
  siteIds: string[];
  reportIds: string[];
}

interface FiscalReport {
  guid: string;
  companyId: string;
  state: string;
}

@Component
export default class FiscalReportDialog extends Vue {
  @Prop({type: Boolean, default: false})
  private value!: boolean;

  private loading = false;
  private showOnlySitesWithReports = false;
  private rawSites: LocationEfsta[] = [];
  private selectedSites: UniqueSite[] = [];
  private uniqueSites: UniqueSite[] = [];
  private fiscalReports: FiscalReport[] = [];
  private confirmDialogVisible = false;
  private downloadProgressDialogVisible = false;
  private downloadedCount = 0;
  private totalReportsToDownload = 0;
  private downloadProgress = 0;

  // Variables pour l'API
  private EFSTA_BASE_URL = "https://portal-api.efsta.net";
  private API_KEY = "public-M7LFeCgiXCuzqLDor6fVHZctGbWuEBsrGdQ";
  private companyId = "108683"; // ID de Circle K

  @Watch("value")
  public onVisibilityChanged(visible: boolean) {
    if (visible) {
      this.fetchSites();
    }
  }

  private async fetchSites() {
    try {
      this.loading = true;
      this.showOnlySitesWithReports = false;
      this.selectedSites = [];
      this.uniqueSites = [];
      this.rawSites = [];

      // Étape 1: Récupérer les sites
      const sitesResponse = await axios.get(
          `${this.EFSTA_BASE_URL}/Public/Company/${this.companyId}/Tree?getLocations=true`,
          {
            headers: {
              "api_key": this.API_KEY,
              "Content-Type": "application/json"
            }
          }
      );

      this.rawSites = sitesResponse.data.rootNode.children || [];

      // Étape 2: Regrouper les sites par nom pour éviter les doublons
      const siteMap = new Map<string, UniqueSite>();

      this.rawSites.forEach((site) => {
        if (!siteMap.has(site.objectName)) {
          siteMap.set(site.objectName, {
            ...site,
            reportCount: 0,
            siteIds: [site.objectId],
            reportIds: [] // Initialiser le tableau des IDs de rapports
          });
        } else {
          const existingSite = siteMap.get(site.objectName)!;
          existingSite.siteIds.push(site.objectId);
        }
      });

      // Étape 3: Récupérer les rapports fiscaux
      const reportsResponse = await axios.get(
          `${this.EFSTA_BASE_URL}/Public/Fiscal/DE/Reporting/List/${this.companyId}`,
          {
            headers: {
              "api_key": this.API_KEY,
              "Content-Type": "application/json"
            }
          }
      );

      this.fiscalReports = reportsResponse.data.reportingList || [];

      // Étape 4: Associer les rapports aux sites et compter le nombre de rapports par site
      this.fiscalReports.forEach((report) => {
        if (report.state === "CREATED") {
          // Parcourir tous les sites uniques
          siteMap.forEach((site) => {
            // Vérifier si l'ID de la société du rapport correspond à l'un des IDs du site
            if (site.siteIds.includes(report.companyId.toString())) {
              site.reportCount++;
              site.reportIds.push(report.guid); // Stocker l'ID du rapport
            }
          });
        }
      });

      // Convertir la Map en tableau
      this.uniqueSites = Array.from(siteMap.values());

    } catch (error) {
      console.error("Error fetching data:", error);
      this.$emit("error", "Failed to fetch sites or reports data");
    } finally {
      this.loading = false;
    }
  }

  private selectAllSites() {
    this.selectedSites = [...this.uniqueSites];
  }

  private deselectAllSites() {
    this.selectedSites = [];
  }

  private removeSite(site: UniqueSite) {
    this.selectedSites = this.selectedSites.filter((s) => s.objectName !== site.objectName);
  }

  private confirmDownload() {
    if (this.selectedSites.length > 0 && this.totalReportsCount > 0) {
      this.confirmDialogVisible = true;
    }
  }

  private async proceedWithDownload() {
    this.confirmDialogVisible = false;
    await this.downloadFiscalReports();
  }

  private async downloadFiscalReports() {
    if (!this.selectedSites.length || !this.totalReportsCount) {
      return;
    }

    try {
      this.loading = true;
      this.downloadProgressDialogVisible = true;
      this.downloadedCount = 0;
      this.downloadProgress = 0;

      // Récupérer tous les IDs des rapports pour les sites sélectionnés
      const reportIds: string[] = [];
      this.selectedSites.forEach((site) => {
        reportIds.push(...site.reportIds);
      });

      this.totalReportsToDownload = reportIds.length;

      if (this.totalReportsToDownload === 0) {
        this.$emit("error", "Aucun rapport disponible pour les sites sélectionnés");
        this.downloadProgressDialogVisible = false;
        this.loading = false;
        return;
      }

      // Créer un nouveau fichier ZIP
      const zip = new JSZip();

      // Télécharger chaque rapport un par un
      for (const reportId of reportIds) {
        try {
          // Appel à l'API pour obtenir un rapport fiscal spécifique
          const response = await axios.get(
              `${this.EFSTA_BASE_URL}/Public/Fiscal/DE/Reporting/${reportId}?acceptDisclaimer=true`,
              {
                headers: {
                  "api_key": this.API_KEY,
                  "Content-Type": "application/json"
                },
                responseType: "arraybuffer"
              }
          );

          // Ajouter le fichier XML au ZIP
          zip.file(`report-${reportId}.xml`, response.data);

          // Mettre à jour le compteur et la progression
          this.downloadedCount++;
          this.downloadProgress = (this.downloadedCount / this.totalReportsToDownload) * 100;
        } catch (error) {
          console.error(`Error downloading report ${reportId}:`, error);
          // Continuer avec les autres rapports même si l'un échoue
        }
      }

      // Générer le ZIP final et le télécharger
      const zipBlob = await zip.generateAsync({type: "blob"});
      saveAs(zipBlob, "fiscal-reports.zip");

      this.showFiscalReportDialog = false;
    } catch (error) {
      console.error("Error during download process:", error);
      this.$emit("error", "Failed to download fiscal reports");
    } finally {
      this.downloadProgressDialogVisible = false;
      this.loading = false;
    }
  }

  get filteredSites(): UniqueSite[] {
    if (this.showOnlySitesWithReports) {
      return this.uniqueSites.filter((site) => site.reportCount > 0);
    }
    return this.uniqueSites;
  }

  get totalReportsCount(): number {
    return this.selectedSites.reduce((total, site) => total + site.reportCount, 0);
  }

  get showFiscalReportDialog(): boolean {
    return this.value;
  }

  set showFiscalReportDialog(value: boolean) {
    this.$emit("input", value);
  }
}
